// Frozen analysis inputs are independent of today's uploaded inventory.
// Unknown or incompatible evidence must never become a claim of full coverage.
const DOCUMENT_REASONS = new Set([
  "no_extractable_text",
  "pages_without_text",
  "extraction_failed",
  "encrypted_document",
  "invalid_document",
  "download_failed",
  "page_limit",
  "text_limit",
  "document_limit",
  "deadline_exceeded",
  "extraction_unavailable",
  "resource_limit",
]);

/** @param {any} value @param {number} [max] */
const count = (value, max = 2147483647) =>
  Number.isSafeInteger(value) && value >= 0 && value <= max;

/** @param {any} value @param {string[]} keys */
const exactKeys = (value, keys) =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.keys(value).length === keys.length &&
  keys.every((key) => Object.prototype.hasOwnProperty.call(value, key));

/** @param {any} value @param {number} max */
const label = (value, max) =>
  typeof value === "string" &&
  value.trim().length > 0 &&
  value.length <= max &&
  !Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return code < 32 || (code >= 127 && code <= 159);
  });

/** @param {any} value */
const validPhotoSet = (value) => {
  if (
    !count(value?.inventory_count, 40) ||
    !count(value?.supplied_count, value.inventory_count) ||
    !Array.isArray(value.supplied) ||
    value.supplied.length !== value.supplied_count
  )
    return false;
  const names = new Set();
  for (const row of value.supplied) {
    if (
      !exactKeys(row, ["filename", "source_fingerprint"]) ||
      typeof row.filename !== "string" ||
      !/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(row.filename) ||
      names.has(row.filename) ||
      typeof row.source_fingerprint !== "string" ||
      !/^[a-f0-9]{64}$/.test(row.source_fingerprint)
    )
      return false;
    names.add(row.filename);
  }
  return true;
};

/** @param {any} value */
const validPhotos = (value) => {
  const importKeys = [
    "source_found_count",
    "imported_count",
    "import_selected_count",
    "import_failed_count",
    "import_limit_omitted_count",
  ];
  if (
    !exactKeys(value, ["inventory_count", "supplied_count", "supplied", ...importKeys]) ||
    !validPhotoSet(value) ||
    importKeys.some((key) => value[key] !== null && !count(value[key]))
  )
    return false;
  const knownImports = importKeys.filter((key) => value[key] !== null).length;
  if (knownImports !== 0 && knownImports !== importKeys.length) return false;
  const found = value.source_found_count;
  const selected = value.import_selected_count;
  const imported = value.imported_count;
  const failed = value.import_failed_count;
  const omitted = value.import_limit_omitted_count;
  return !(
    (found !== null && selected !== null && selected > found) ||
    (selected !== null && imported !== null && imported > selected) ||
    (selected !== null && failed !== null && failed > selected) ||
    (selected !== null && imported !== null && failed !== null && imported + failed > selected) ||
    (found !== null && selected !== null && omitted !== null && omitted !== found - selected)
  );
};

/** @param {any} rows */
const validDocuments = (rows) => {
  if (!Array.isArray(rows) || rows.length > 30) return false;
  const ids = new Set();
  let included = 0;
  for (const [index, row] of rows.entries()) {
    if (
      !exactKeys(row, [
        "id",
        "filename",
        "status",
        "page_count",
        "pages_examined",
        "pages_with_text",
        "chars_included",
        "reason_codes",
      ]) ||
      !label(row.id, 64) ||
      !label(row.filename, 200) ||
      ids.has(row.id) ||
      !["read", "partial", "unreadable", "omitted"].includes(row.status) ||
      (row.page_count !== null && !count(row.page_count)) ||
      !count(row.pages_examined, 50) ||
      (row.page_count !== null && row.pages_examined > row.page_count) ||
      !count(row.pages_with_text, row.pages_examined) ||
      (row.status === "read" &&
        (row.page_count === null ||
          row.page_count < 1 ||
          row.pages_examined !== row.page_count ||
          row.pages_with_text !== row.pages_examined)) ||
      !count(row.chars_included, 12000) ||
      (row.chars_included > 0 && row.pages_with_text === 0) ||
      (["read", "partial"].includes(row.status)
        ? row.chars_included === 0
        : row.chars_included !== 0) ||
      !Array.isArray(row.reason_codes) ||
      (row.status === "read" ? row.reason_codes.length !== 0 : row.reason_codes.length === 0) ||
      row.reason_codes.some((/** @type {any} */ code) => !DOCUMENT_REASONS.has(code)) ||
      new Set(row.reason_codes).size !== row.reason_codes.length ||
      (index >= 3 &&
        (row.status !== "omitted" ||
          !row.reason_codes.includes("document_limit") ||
          row.pages_examined !== 0 ||
          row.pages_with_text !== 0 ||
          row.chars_included !== 0))
    )
      return false;
    ids.add(row.id);
    included += row.chars_included;
  }
  return included <= 12000;
};

/** @param {any} value */
export function sourceCoverage(value) {
  if (
    !exactKeys(value, ["version", "photos", "documents", "last_photo_revision"]) ||
    value.version !== 1 ||
    (value.photos !== null && !validPhotos(value.photos)) ||
    (value.documents !== null && !validDocuments(value.documents))
  )
    return null;
  const revision = value.last_photo_revision;
  if (
    revision !== null &&
    (!exactKeys(revision, ["section_id", "inventory_count", "supplied_count", "supplied"]) ||
      !label(revision.section_id, 128) ||
      !validPhotoSet(revision))
  )
    return null;
  return value;
}

/** Only compare against a complete, validated frozen document inventory.
 * @param {any} coverage @param {any} inventory
 */
export function documentsNotInReport(coverage, inventory) {
  const valid = sourceCoverage(coverage);
  if (!valid || valid.documents === null || !Array.isArray(inventory)) return [];
  const used = new Set(valid.documents.map((/** @type {any} */ row) => row.id));
  const seen = new Set();
  return inventory.slice(0, 30).filter((row) => {
    if (!label(row?.id, 64) || !label(row?.filename, 200) || used.has(row.id) || seen.has(row.id))
      return false;
    seen.add(row.id);
    return true;
  });
}

/** @param {any} state */
export function importPhotoCounts(state) {
  if (
    !count(state?.photos_total, 10) ||
    !count(state?.photos_complete, state.photos_total) ||
    !count(state?.photos_failed, state.photos_total) ||
    state.photos_complete + state.photos_failed > state.photos_total ||
    (state.source_photo_count != null &&
      (!count(state.source_photo_count) || state.source_photo_count < state.photos_total))
  )
    return null;
  return {
    selected: state.photos_total,
    saved: state.photos_complete,
    failed: state.photos_failed,
    found: state.source_photo_count ?? null,
  };
}

/** @param {any} row */
export function documentCoverageNotes(row) {
  /** @type {Record<string, string>} */
  const messages = {
    no_extractable_text:
      "No usable PDF text layer was found; scanned pages may need a text-based copy.",
    pages_without_text:
      "Some examined pages had no extractable text; diagrams and scanned content may be missing.",
    extraction_failed: "Some document text could not be extracted.",
    encrypted_document: "The PDF was encrypted and could not be read.",
    invalid_document: "The PDF could not be read.",
    download_failed: "The document could not be retrieved for this analysis.",
    page_limit: "The 50-page-per-document reading limit was reached.",
    text_limit: "The shared 12,000-character document-text limit was reached.",
    document_limit: "The three-document reading limit was reached.",
    deadline_exceeded: "The document could not be fully read within the available processing time.",
    extraction_unavailable: "Document text extraction was unavailable.",
    resource_limit: "A safe document-processing limit was reached.",
  };
  return row.reason_codes.map((/** @type {string} */ code) => messages[code]);
}
